/**
 * DocumentManagementPanel - Panel for managing document approval workflow
 * Shows pending documents with approve/reject actions and bulk operations
 *
 * TASK-083: Refactored to use httpClient for gateway enforcement
 */

import React, { useState, useEffect } from 'react';
import { httpClient } from '../lib/httpClient';

interface Document {
  upload_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  template_type: string;
  upload_status: string;
  total_rows: number;
  error_message?: string;
  created_at: string;
  updated_at?: string;
  extraction_method?: string;
  extraction_confidence?: number;
  validation_errors: Array<any>;
  error_percentage: number;
  detected_template_type?: string;
  template_confidence?: number;
  unmapped_fields: Array<string>;
}

interface DocumentManagementPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentAction: (uploadId: string, action: 'approved' | 'rejected' | 'removed') => void;
  clientId?: string;
}

const DocumentManagementPanel: React.FC<DocumentManagementPanelProps> = ({
  isOpen,
  onClose,
  onDocumentAction,
  clientId = 'default-client'
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      fetchPendingDocuments();
    }
  }, [isOpen, clientId]);

  const fetchPendingDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await httpClient.get<any>(`/uploads/approval-queue?client_id=${clientId}`);
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentAction = async (uploadId: string, action: 'approve' | 'reject' | 'remove', reason?: string) => {
    try {
      setActionLoading(prev => ({ ...prev, [uploadId]: true }));

      if (action === 'approve') {
        await httpClient.post(`/uploads/${uploadId}/confirm`);
      } else if (action === 'reject') {
        await httpClient.post(`/uploads/${uploadId}/reject`, {
          reason: reason || 'Quality issues',
          user_id: 'current_user', // TODO: Get from auth context
          notes: ''
        });
      } else { // remove
        await httpClient.delete(`/uploads/${uploadId}`);
      }

      // Remove from local state
      setDocuments(prev => prev.filter(doc => doc.upload_id !== uploadId));
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
      onDocumentAction(uploadId, action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'removed');
    } catch (err: any) {
      console.error(`Error ${action}ing document:`, err);
      const errorMessage = err?.response?.data?.detail || err?.message || `Failed to ${action} document`;
      alert(`Failed to ${action} document: ${errorMessage}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [uploadId]: false }));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedDocuments.size === 0) return;

    try {
      setBulkActionLoading(true);
      const result = await httpClient.post<any>('/uploads/bulk-approve', {
        upload_ids: Array.from(selectedDocuments),
        user_id: 'current_user', // TODO: Get from auth context
        notes: 'Bulk approval from document management panel'
      });

      // Remove successful approvals from local state
      if (result.details.approved) {
        const approvedIds = result.details.approved.map((item: any) => item.upload_id);
        setDocuments(prev => prev.filter(doc => !approvedIds.includes(doc.upload_id)));
        approvedIds.forEach((id: string) => onDocumentAction(id, 'approved'));
      }
      setSelectedDocuments(new Set());

      if (result.details.failed && result.details.failed.length > 0) {
        alert(`${result.details.approved.length} documents approved. ${result.details.failed.length} failed.`);
      }
    } catch (err: any) {
      console.error('Error in bulk approval:', err);
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert(`Bulk approval failed: ${errorMessage}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleDocumentSelection = (uploadId: string) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uploadId)) {
        newSet.delete(uploadId);
      } else {
        newSet.add(uploadId);
      }
      return newSet;
    });
  };

  const selectAllDocuments = () => {
    setSelectedDocuments(new Set(documents.map(doc => doc.upload_id)));
  };

  const clearSelection = () => {
    setSelectedDocuments(new Set());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'ingesting':
        return 'bg-blue-100 text-blue-800';
      case 'complete':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return '✅';
      case 'error':
        return '❌';
      case 'processing':
        return '⏳';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      case 'ingesting':
        return '⏳';
      case 'complete':
        return '🎉';
      default:
        return '📄';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ready':
        return 'Ready for Review';
      case 'error':
        return 'Processing Error';
      case 'processing':
        return 'Processing...';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'ingesting':
        return 'Importing...';
      case 'complete':
        return 'Import Complete';
      default:
        return status;
    }
  };

  const getQualityColor = (errorPercentage: number) => {
    if (errorPercentage <= 5) return 'bg-green-100 text-green-800';
    if (errorPercentage <= 15) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      
      {/* Panel */}
      <div className="fixed top-0 right-0 w-2/3 h-full bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-kielis-navy text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Document Management</h2>
            <p className="text-blue-100 text-sm">Review and approve pending documents</p>
          </div>
          <button 
            onClick={onClose}
            className="text-white hover:text-blue-200 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-kielis-navy border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={fetchPendingDocuments}
                  className="mt-2 text-red-600 underline hover:text-red-700"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No pending documents</h3>
                <p className="text-gray-600">All documents have been processed or are still uploading.</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Bulk Actions */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {selectedDocuments.size} of {documents.length} selected
                    </span>
                    <button 
                      onClick={selectAllDocuments}
                      className="text-blue-600 hover:text-blue-700 text-sm underline"
                    >
                      Select all
                    </button>
                    <button 
                      onClick={clearSelection}
                      className="text-gray-600 hover:text-gray-700 text-sm underline"
                    >
                      Clear selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkApprove}
                    disabled={selectedDocuments.size === 0 || bulkActionLoading}
                    className="bg-kielis-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {bulkActionLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Approving...
                      </>
                    ) : (
                      <>
                        ✓ Approve Selected ({selectedDocuments.size})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Document List */}
              <div className="space-y-4">
                {documents.map((doc) => (
                  <div key={doc.upload_id} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.has(doc.upload_id)}
                          onChange={() => toggleDocumentSelection(doc.upload_id)}
                          className="mt-1 h-4 w-4 text-kielis-navy focus:ring-kielis-navy border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">{doc.filename}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <span className={`ml-1 px-2 py-1 rounded text-xs ${getStatusColor(doc.upload_status)} flex items-center gap-1`}>
                                {getStatusIcon(doc.upload_status)} {getStatusText(doc.upload_status)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Rows:</span>
                              <span className="ml-1 font-medium">{doc.total_rows?.toLocaleString() || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Template:</span>
                              <span className="ml-1 font-medium">{doc.template_type}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Quality:</span>
                              <span className={`ml-1 px-2 py-1 rounded text-xs ${getQualityColor(doc.error_percentage)}`}>
                                {doc.error_percentage.toFixed(1)}% errors
                              </span>
                            </div>
                          </div>
                          
                          {doc.validation_errors.length > 0 && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-yellow-800 text-sm font-medium">
                                {doc.validation_errors.length} validation issues found
                              </p>
                              <p className="text-yellow-700 text-xs mt-1">
                                Click "Review & Fix" to see details and make corrections
                              </p>
                            </div>
                          )}
                          
                          {doc.error_message && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                              <p className="text-red-800 text-sm">{doc.error_message}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleDocumentAction(doc.upload_id, 'approve')}
                          disabled={actionLoading[doc.upload_id]}
                          className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {actionLoading[doc.upload_id] ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            '✓'
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Reason for rejection:');
                            if (reason) {
                              handleDocumentAction(doc.upload_id, 'reject', reason);
                            }
                          }}
                          disabled={actionLoading[doc.upload_id]}
                          className="bg-red-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {actionLoading[doc.upload_id] ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            '×'
                          )}
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Are you sure you want to permanently remove "${doc.filename}"?`)) {
                              handleDocumentAction(doc.upload_id, 'remove');
                            }
                          }}
                          disabled={actionLoading[doc.upload_id]}
                          className="bg-gray-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          🗑️ Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {documents.length} documents pending approval
            </div>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentManagementPanel;